/**
 * IndexedDB storage for dream analyses
 * Persists across browser sessions and cache clears
 */

import type { AnalysisResults } from '../types/frontend';

const DB_NAME = 'sailu_dreams';
const DB_VERSION = 1;
const STORE_NAME = 'dream_analyses';

interface StoredDream {
  id: string;
  timestamp: number;
  dreamText: string;
  analysis: AnalysisResults;
}

class DreamStorageService {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ IndexedDB initialized');
        resolve();
      };

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('📦 Created dream analyses object store');
        }
      };
    });
  }

  async saveDream(dreamText: string, analysis: AnalysisResults): Promise<string> {
    if (!this.db) await this.init();

    const id = `dream_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const storedDream: StoredDream = {
      id,
      timestamp: Date.now(),
      dreamText: dreamText.substring(0, 500), // Store truncated version for list
      analysis,
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add(storedDream);

      request.onsuccess = () => {
        console.log(`💾 Saved dream ${id}`);
        resolve(id);
      };

      request.onerror = () => {
        console.error('Failed to save dream:', request.error);
        reject(request.error);
      };
    });
  }

  async getAllDreams(): Promise<StoredDream[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('timestamp');
      const request = index.openCursor(null, 'prev'); // Newest first

      const dreams: StoredDream[] = [];

      request.onsuccess = event => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          dreams.push(cursor.value);
          cursor.continue();
        } else {
          resolve(dreams);
        }
      };

      request.onerror = () => {
        console.error('Failed to load dreams:', request.error);
        reject(request.error);
      };
    });
  }

  async getDream(id: string): Promise<StoredDream | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        console.error('Failed to get dream:', request.error);
        reject(request.error);
      };
    });
  }

  async deleteDream(id: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => {
        console.log(`🗑️ Deleted dream ${id}`);
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to delete dream:', request.error);
        reject(request.error);
      };
    });
  }

  async getDreamCount(): Promise<number> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.count();

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }
}

// Singleton instance
export const dreamStorage = new DreamStorageService();
export type { StoredDream };
